/**
 * Who did what, and when — every change the league has a record of, on one timeline.
 *
 * ADMIN ONLY, and that is not decoration. This screen answers "who moved my keeper", "when did
 * that FAAB line appear" and "has anybody been in here" — questions that are legitimate for the
 * man who runs the league and nobody else's business. The gate is `admin`, not `commissioner`:
 * commissioners are the people the log is most often ABOUT.
 *
 * TWO HALVES, AND THEY ARE DIFFERENT KINDS OF TRUTH.
 *
 *   DERIVED (this file). Every record in the database already carries its own provenance — a
 *     keeper slate has `updatedBy`/`submittedAt`, a FAAB line has `recordedBy`, a trade has
 *     `recordedAt`, a proposal has `createdAt` and a vote has `castBy`. Reading them produces a
 *     complete history of everything that currently EXISTS, retroactively, back through 2025, with
 *     no new writes anywhere and no possibility of drifting from the records it describes. It IS
 *     the records.
 *
 *   RECORDED (`store.audit`). What derivation cannot see: a sign-in, a deletion, a page view, and
 *     the fact that something was edited three times rather than once. Those are written as they
 *     happen and only exist from the day the log ships.
 *
 * The two are merged and labelled, so a reader can tell "this is what the record says" from "this
 * is what somebody's browser reported". Where they overlap the derived event wins: a stored row is
 * a claim, and the record it describes is the thing itself.
 *
 * WHAT IS DELIBERATELY NOT HERE. No IP addresses, no user agents, no read of anybody's private
 * mock-keeper set. The log says a page was opened, not what was on it.
 */

import type {
  AuditEntry, Avatar, DraftPick, DraftPreference, DraftState, FaabEntry, FarmNomination,
  InterpretationDoc, KeeperDeclaration, LeagueManager, LeagueSettings, LiveTradeAsset, LiveTradeDoc,
  RuleProposal,
  TeamPairing, Trade,
} from '../store/types.js';
import type { Override } from './overrides.js';
import { dayIn } from './deadlines.js';

/* -- the shape of one line --------------------------------------------------------------------- */

export type EventKind =
  | 'sign-in' | 'view'
  | 'keeper' | 'faab' | 'trade' | 'proposal' | 'vote' | 'interpretation' | 'override'
  | 'pick' | 'farm' | 'settings' | 'access' | 'roster' | 'avatar' | 'draft' | 'delete'
  | 'acting-as' | 'note';

/** One field of an event, for the sheet a row opens. */
export interface Detail { label: string; value: string }

export interface AuditEvent {
  /** Stable across re-derivations, so React keys and de-duplication both work. */
  id: string;
  at: string;
  /** The account that did it. A GM id once paired; a raw uid before that. */
  actor: string;
  /** Whose record it was, when somebody worked on another manager's behalf. */
  onBehalfOf?: string;
  kind: EventKind;
  /** One line, already written for a person to read. */
  what: string;
  /** `collection/id`, so a row can be traced to the record it describes. */
  target?: string;
  source: 'derived' | 'recorded';
  /**
   * Everything the one-line sentence had to leave out. A log row is a scannable summary; the
   * question that follows it is always "what exactly changed", and until this existed the answer
   * was to go and open the record.
   */
  detail?: Detail[];
  /**
   * THE REAL HAND, when it was not the man the record names.
   *
   * `onBehalfOf` is the other direction: the record says who saved it and this is whose record it
   * was. `via` is for records that carry no saver at all — a farm nomination knows whose it is and
   * (until today) not who typed it — and it is filled in by overlapping the event with a recorded
   * acting-as span. That is an INFERENCE, and `viaInferred` says so, because a log that presented
   * a guess and a fact identically would be worse than not guessing.
   */
  via?: string;
  /** True where `via` is a guess from an acting-as span rather than a fact. */
  viaInferred?: boolean;
  /** Set where an admin said so afterwards: `via` is his claim, and this names when. */
  viaNotedAt?: string;
  /** Why, in his words. */
  viaWhy?: string;
}

export const KIND_LABEL: Readonly<Record<EventKind, string>> = {
  'sign-in': 'Sign-in',
  view: 'Viewed',
  keeper: 'Keepers',
  faab: 'FAAB',
  trade: 'Trade',
  proposal: 'Proposal',
  vote: 'Vote',
  interpretation: 'Interpretation',
  override: 'Override',
  pick: 'Draft pick',
  farm: 'Farm auction',
  settings: 'Settings',
  access: 'Access',
  roster: 'Roster',
  avatar: 'Avatar',
  draft: 'Draft board',
  delete: 'Deleted',
  'acting-as': 'Acting as',
  note: 'Noted',
};

/** Everything the screen can hand the deriver. All optional: a store may not have loaded it. */
export interface AuditSources {
  declarations?: readonly KeeperDeclaration[];
  preferences?: readonly DraftPreference[];
  trades?: readonly Trade[];
  liveTrades?: readonly LiveTradeDoc[];
  faab?: readonly FaabEntry[];
  proposals?: readonly RuleProposal[];
  interpretations?: readonly InterpretationDoc[];
  overrides?: readonly Override[];
  picks?: readonly DraftPick[];
  farm?: readonly FarmNomination[];
  settings?: LeagueSettings | null;
  draftState?: DraftState | null;
  managers?: readonly LeagueManager[];
  pairings?: readonly TeamPairing[];
  avatars?: readonly Avatar[];
}

/* -- deriving ---------------------------------------------------------------------------------- */

const ok = (s: string | null | undefined): s is string => typeof s === 'string' && s.length > 0;

function ev(
  id: string, at: string, actor: string, kind: EventKind, what: string,
  extra: { target?: string; onBehalfOf?: string; detail?: Array<Detail | null> } = {},
): AuditEvent {
  return {
    id, at, actor: actor.toUpperCase(), kind, what, source: 'derived',
    ...(extra.target ? { target: extra.target } : {}),
    ...(extra.onBehalfOf && extra.onBehalfOf.toUpperCase() !== actor.toUpperCase()
      ? { onBehalfOf: extra.onBehalfOf.toUpperCase() }
      : {}),
    // Nulls are dropped rather than rendered blank: an empty value in a detail sheet reads as a
    // field that was cleared, which is a different fact from one the record never had.
    ...(extra.detail?.filter((d): d is Detail => d !== null).length
      ? { detail: extra.detail.filter((d): d is Detail => d !== null) }
      : {}),
  };
}

/** A field, or nothing when the record has no answer. */
const d = (label: string, value: string | number | null | undefined): Detail | null =>
  (value === null || value === undefined || value === '' ? null : { label, value: String(value) });

const n = (x: number) => (x === 1 ? '' : 's');

/**
 * A keeper slate, spelled out.
 *
 * The one-line summary can only say how many, and "3 keepers" is not the answer to "what did he
 * change" — the interesting slate edits keep the count and swap a name, or move one to a different
 * round. Each keeper carries the things that cost him something: the franchise tag, an elevation,
 * and a forfeited farm graduate, who stays on the slate and is still taxed.
 */
function slate(dec: KeeperDeclaration): Array<Detail | null> {
  return [
    d('Status', dec.status),
    ...dec.keepers.map((k, i) => d(`Keeper ${i + 1}`, [
      k.player,
      k.franchiseTagged ? 'franchise tag' : null,
      k.farmForfeited ? 'farm forfeit' : null,
      k.elevatedTo ? `moved to round ${k.elevatedTo}` : null,
      k.chargedPick ? `charged ${k.chargedPick.round} (${k.chargedPick.from})` : null,
    ].filter(Boolean).join(' · '))),
    d('Note', dec.note),
  ];
}

/** What one side of a live trade sends, in one line. */
function liveSide(side: { gm: string; sends: readonly LiveTradeAsset[] }): string {
  if (!side.sends.length) return 'nothing';
  return side.sends.map((a) => (a.kind === 'clause' ? `clause: ${a.text}` : a.text)).join(' · ');
}

/** What one side of a ledger trade received, in one line. */
function ledgerSide(side: Trade['sides'][number]): string {
  const bits = [
    ...side.players,
    ...side.rights.map((r) => `rights to ${r}`),
    ...side.picks,
    ...(side.faab ? [`₣${side.faab}`] : []),
  ];
  return bits.length ? bits.join(' · ') : 'nothing recorded';
}

/**
 * Every change the database still holds evidence of.
 *
 * A record with no timestamp produces NO event rather than one dated today. An undated line at the
 * top of a chronological log is worse than a missing one: it reads as something that just
 * happened, and six of the 2025 trades have no date at all.
 */
export function deriveEvents(s: AuditSources): AuditEvent[] {
  const out: AuditEvent[] = [];

  /*
   * KEEPERS. Two events from one record where both stamps exist, because submitting is a
   * different act from saving a draft — it is the one that binds — and an admin looking for
   * "when did Ryan commit" must not have to infer it from an `updatedAt` that has since moved.
   */
  for (const dec of s.declarations ?? []) {
    const who = dec.updatedBy ?? dec.gm;
    if (ok(dec.submittedAt)) {
      out.push(ev(`kd-sub-${dec.season}-${dec.gm}`, dec.submittedAt, who, 'keeper',
        `Submitted ${dec.keepers.length} keeper${n(dec.keepers.length)} for ${dec.season}`,
        { target: `declarations/${dec.season}-${dec.gm}`, onBehalfOf: dec.gm, detail: slate(dec) }));
    }
    if (ok(dec.updatedAt) && dec.updatedAt !== dec.submittedAt) {
      out.push(ev(`kd-upd-${dec.season}-${dec.gm}`, dec.updatedAt, who, 'keeper',
        `${dec.status === 'submitted' ? 'Changed a submitted slate' : 'Saved a draft slate'}`
        + ` — ${dec.keepers.length} keeper${n(dec.keepers.length)}`,
        { target: `declarations/${dec.season}-${dec.gm}`, onBehalfOf: dec.gm, detail: slate(dec) }));
    }
  }

  for (const p of s.preferences ?? []) {
    if (!ok(p.updatedAt)) continue;
    out.push(ev(`pref-${p.season}-${p.gm}`, p.updatedAt, p.gm, 'draft',
      'Set his draft-position preferences', {
        target: `preferences/${p.season}-${p.gm}`,
        detail: [d('Ranking', p.ranked?.join(' · ')), d('Submitted', p.submittedAt)],
      }));
  }

  for (const t of s.trades ?? []) {
    if (!ok(t.recordedAt)) continue;
    const parties = t.sides.map((x) => x.gm).join(' and ');
    out.push(ev(`tr-${t.id}`, t.recordedAt, t.recordedBy, t.voided ? 'delete' : 'trade',
      t.voided ? `Struck a trade from the log — ${parties}` : `Recorded a trade — ${parties}`,
      {
        target: `trades/${t.id}`,
        detail: [
          d('Season', t.season), d('Dated', t.date),
          ...t.sides.map((x) => d(`${x.gm} got`, ledgerSide(x))),
          d('Note', t.note), d('Struck', t.voided ? 'yes' : null),
        ],
      }));
  }

  /*
   * LIVE TRADES produce up to four events, because a trade in flight is the one object in this
   * app that several different people act on in sequence — and "who affirmed it" is exactly the
   * question that gets asked afterwards.
   */
  for (const t of s.liveTrades ?? []) {
    const parties = t.sides.map((x) => x.gm).join(' and ');
    if (ok(t.filedAt)) {
      out.push(ev(`lt-file-${t.id}`, t.filedAt, t.filedBy, 'trade',
        `Filed a trade — ${parties}`, {
          target: `liveTrades/${t.id}`,
          detail: [
            ...t.sides.map((x) => d(`${x.gm} sends`, liveSide(x))),
            d('Clauses', t.clauses.join(' · ')),
          ],
        }));
    }
    if (ok(t.agreedAt)) {
      out.push(ev(`lt-agree-${t.id}`, t.agreedAt, t.agreedBy ?? t.sides[1].gm, 'trade',
        t.agreedSource === 'commissioner'
          ? `Recorded an agreed time for a trade struck outside the app — ${parties}`
          : `Affirmed a trade — ${parties}`,
        {
          target: `liveTrades/${t.id}`,
          detail: [
            d('Clock starts', t.agreedAt), d('Source', t.agreedSource),
            d('Recorded by', t.agreedBy),
            // The window everything else on that screen is measured against.
            d('Counterparty', t.sides[1].gm),
          ],
        }));
    }
    for (const c of t.counters) {
      if (!ok(c.at)) continue;
      out.push(ev(`lt-ctr-${t.id}-${c.id}`, c.at, c.from, 'trade',
        `${c.kind === 'match' ? 'Put a match in' : 'Sent a counter-offer'} to ${c.to}`,
        {
          target: `liveTrades/${t.id}`,
          detail: [
            d('To', c.to), d('He sends', liveSide({ gm: c.from, sends: c.sends })),
            d('He receives', liveSide({ gm: c.to, sends: c.receives })),
            d('State', c.state), d('Kind', c.kind ?? 'counter'),
          ],
        }));
    }
    /*
     * A COMMISSIONER ENDING A WINDOW EARLY IS AN ACT, not a state (Tim, 8 September: "add
     * functionality as commish to push the trade through before the window is complete"). It cuts
     * short eight other GMs' right to counter and to veto, so it belongs in the derived history
     * beside filing and affirming rather than living only on the card as a field.
     */
    if (t.forced && ok(t.forced.at)) {
      out.push(ev(`lt-force-${t.id}`, t.forced.at, t.forced.by, 'trade',
        `Closed a trade’s window early — ${parties}`, {
          target: `liveTrades/${t.id}`,
          detail: [
            d('Agreed at', t.agreedAt), d('Closed at', t.forced.at),
            d('Vetoes declared', String(t.vetoes.length)),
            d('Reason', t.forced.reason),
          ],
        }));
    }
    if (t.accepted && ok(t.accepted.at)) {
      out.push(ev(`lt-acc-${t.id}`, t.accepted.at, t.sides[1].gm, 'trade',
        `Accepted a counter-offer — ${parties}`, { target: `liveTrades/${t.id}` }));
    }
  }

  for (const f of s.faab ?? []) {
    if (!ok(f.date)) continue;
    const sign = f.amount < 0 ? '−' : '+';
    out.push(ev(`faab-${f.id}`, f.date, f.recordedBy, 'faab',
      `${f.kind} for ${f.gm}, ${sign}₣${Math.abs(f.amount)} — ${f.description}`,
      {
        target: `faab/${f.id}`,
        onBehalfOf: f.gm,
        detail: [
          d('Manager', f.gm), d('Amount', `${sign}₣${Math.abs(f.amount)}`), d('Kind', f.kind),
          d('Description', f.description), d('Season', f.season),
        ],
      }));
  }

  for (const p of s.proposals ?? []) {
    if (ok(p.createdAt)) {
      /*
       * ATTRIBUTED TO WHOEVER TYPED IT, and the author named beside him. A commissioner
       * transcribing a proposal that arrived by email is not the author — `gm` stays Ari — but
       * the act was the commissioner's, and a log that said "Ari wrote a proposal" about a
       * commissioner's keystrokes would be the same mistake as the farm nomination.
       */
      out.push(ev(`prop-new-${p.id}`, p.createdAt, p.enteredBy ?? p.gm, 'proposal',
        p.enteredBy
          ? `Entered ${p.gm}’s proposal — ${p.title}`
          : `Wrote a proposal — ${p.title}`, {
          target: `proposals/${p.id}`,
          onBehalfOf: p.gm,
          detail: [
            d('Author', p.gm), d('Title', p.title), d('Amends', p.amends?.join(' · ')),
            d('Entered by', p.enteredBy), d('Came in via', p.enteredVia),
            d('Body', p.body),
          ],
        }));
    }
    if (ok(p.updatedAt) && p.updatedAt !== p.createdAt) {
      out.push(ev(`prop-upd-${p.id}`, p.updatedAt, p.gm, 'proposal',
        `Changed a proposal (${p.status}) — ${p.title}`, {
          target: `proposals/${p.id}`,
          detail: [d('Status', p.status), d('Title', p.title), d('Body', p.body)],
        }));
    }
    /*
     * A vote is attributed to the man who CAST it, which is not always the man it belongs to:
     * `castBy[RYAN] === 'RAJAN'` is Rajan recording Ryan's proxy. Both names go on the line,
     * because a log that showed only one of them would be the log getting a proxy wrong.
     */
    for (const [gm, at] of Object.entries(p.castAt ?? {})) {
      if (!ok(at)) continue;
      const by = p.castBy?.[gm] ?? gm;
      out.push(ev(`vote-${p.id}-${gm}`, at, by, 'vote',
        `Recorded ${gm}’s vote on ${p.title}`, {
          target: `proposals/${p.id}`,
          onBehalfOf: gm,
          detail: [
            d('Proposal', p.title), d('Voter', gm), d('Vote', p.votes?.[gm]),
            d('Cast by', by === gm ? null : `${by} — proxy`),
          ],
        }));
    }
  }

  for (const i of s.interpretations ?? []) {
    if (ok(i.raisedAt)) {
      out.push(ev(`int-${i.id}`, i.raisedAt, i.raisedBy, 'interpretation',
        `Asked the panel about ${i.ruleId}`, {
          target: `interpretations/${i.id}`,
          detail: [d('Rule', i.ruleId), d('Section', i.section), d('Question', i.question)],
        }));
    }
    if (ok(i.decidedAt)) {
      out.push(ev(`int-dec-${i.id}`, i.decidedAt, i.raisedBy, 'interpretation',
        `A panel decision was recorded on ${i.ruleId}`, {
          target: `interpretations/${i.id}`,
          detail: [d('Rule', i.ruleId), d('Question', i.question), d('Decision', i.decision)],
        }));
    }
  }

  for (const o of s.overrides ?? []) {
    if (ok(o.at)) {
      out.push(ev(`ovr-${o.id}`, o.at, o.by, 'override',
        `Overrode ${o.label}: ${o.ruleValue} → ${o.setTo} — ${o.reason}`,
        {
          target: `overrides/${o.id}`,
          detail: [
            d('What', o.label), d('Target', o.target), d('The rule says', o.ruleValue),
            d('Set to', o.setTo), d('Reason', o.reason), d('Cites', o.cites?.join(' · ')),
          ],
        }));
    }
    if (ok(o.revertedAt)) {
      out.push(ev(`ovr-rev-${o.id}`, o.revertedAt, o.revertedBy ?? o.by, 'override',
        `Put ${o.label} back to the rule value`, { target: `overrides/${o.id}` }));
    }
  }

  for (const p of s.picks ?? []) {
    if (!ok(p.madeAt)) continue;
    out.push(ev(`pick-${p.season}-${p.overall}`, p.madeAt, p.madeBy ?? p.gm, 'pick',
      `Pick ${p.round}.${p.pick} — ${p.player ?? 'cleared'}${p.isKeeper ? ' (keeper)' : ''}`,
      {
        target: `picks/${p.overall}`,
        onBehalfOf: p.gm,
        detail: [
          d('Overall', p.overall), d('Round', `${p.round}.${p.pick}`), d('Team', p.gm),
          d('Player', p.player), d('Position', p.position), d('NFL team', p.nflTeam),
          d('Keeper', p.isKeeper ? 'yes' : null),
        ],
      }));
  }

  for (const f of s.farm ?? []) {
    if (!ok(f.updatedAt)) continue;
    const what = f.passed ? `Passed on ${f.round}.${f.slot}`
      : f.winner ? `${f.winner} won ${f.player ?? 'a nomination'} for ₣${f.bid ?? 0}`
        : `Nominated ${f.player ?? 'a prospect'} at ${f.round}.${f.slot}`;
    /*
     * `updatedBy` OVER `nominator`, and the difference is the whole point. Draft night is one
     * commissioner typing for a room, so the man whose nomination it is frequently is not the man
     * who entered it. Rows written before that field existed have only `nominator`; those are the
     * ones `attributeVia` can still rescue from a recorded acting-as span.
     */
    out.push(ev(`farm-${f.season}-${f.round}-${f.slot}`, f.updatedAt,
      f.updatedBy ?? f.nominator, 'farm', what, {
        target: `farm/${f.round}.${f.slot}`,
        onBehalfOf: f.nominator,
        detail: [
          d('Lot', `${f.round}.${f.slot}`), d('Nominator', f.nominator), d('Prospect', f.player),
          d('Position', f.position), d('School', f.school),
          d('Winner', f.winner), d('Sold for', f.bid !== undefined ? `₣${f.bid}` : null),
          d('High bid', f.highBid !== undefined ? `₣${f.highBid} (${f.highBidder ?? '—'})` : null),
          d('Passed', f.passed ? 'yes' : null),
          d('Passed by', f.passedBy?.join(' · ')),
          d('Entered by', f.updatedBy),
        ],
      }));
  }

  if (s.settings && ok(s.settings.updatedAt)) {
    out.push(ev(`set-${s.settings.season}`, s.settings.updatedAt, s.settings.updatedBy, 'settings',
      `Changed the ${s.settings.season} league settings`, {
        target: 'meta/settings',
        detail: [
          d('Keeper deadline', s.settings.keeperDeadline),
          d('Draft', s.settings.draftDate),
          d('Declarations locked', s.settings.declarationsLocked ? 'yes' : null),
        ],
      }));
  }

  if (s.draftState && ok(s.draftState.updatedAt)) {
    out.push(ev(`ds-${s.draftState.season}`, s.draftState.updatedAt, 'COMMISSIONER', 'draft',
      `Draft board is ${s.draftState.status}, on pick ${s.draftState.onTheClock}`,
      { target: 'meta/draftState' }));
  }

  for (const m of s.managers ?? []) {
    if (!ok(m.addedAt)) continue;
    out.push(ev(`mgr-${m.id}`, m.addedAt, m.addedBy, 'roster',
      `Added ${m.id} to the roster`, { target: `managers/${m.id}` }));
  }

  for (const p of s.pairings ?? []) {
    if (!ok(p.pairedAt)) continue;
    out.push(ev(`pair-${p.season}-${p.team}`, p.pairedAt, p.pairedBy, 'roster',
      `Paired “${p.team}” (${p.season})`, { target: `teamPairings/${p.season}-${p.team}` }));
  }

  for (const a of s.avatars ?? []) {
    if (!ok(a.updatedAt)) continue;
    out.push(ev(`av-${a.gm}`, a.updatedAt, a.updatedBy, 'avatar',
      `Set ${a.gm}’s picture`, {
        target: `avatars/${a.gm}`,
        onBehalfOf: a.gm,
        // The picture itself is a data URL of up to 40kB. Its SIZE is the useful fact.
        detail: [d('Manager', a.gm), d('Picture', a.image ? `${Math.round(a.image.length / 1024)} kB` : null)],
      }));
  }

  return out;
}

/* -- merging ----------------------------------------------------------------------------------- */

/** A stored row, in the shape the timeline reads. */
export function fromEntry(e: AuditEntry): AuditEvent {
  return {
    id: e.id,
    at: e.at,
    actor: String(e.actor).toUpperCase(),
    kind: e.kind as EventKind,
    what: e.what,
    source: 'recorded',
    ...(e.target ? { target: e.target } : {}),
    ...(e.onBehalfOf ? { onBehalfOf: e.onBehalfOf.toUpperCase() } : {}),
    ...(e.detail && Object.keys(e.detail).length
      ? { detail: Object.entries(e.detail).map(([label, value]) => ({ label, value })) }
      : {}),
  };
}

/* -- who was really at the keyboard ------------------------------------------------------------ */

/**
 * A stretch of time during which one man was acting as another.
 *
 * `viewAs` is how an admin looks at the league as somebody else, and everything the app writes
 * while it is on names the manager being impersonated — which is right for the DATA and wrong for
 * the log. Some records carry a saver field and get this correct on their own (a keeper slate
 * knows `updatedBy`); some carry none at all, and a farm nomination written before 22 August is
 * the case Tim found: "John nominated a prospect" for a row he had entered himself.
 *
 * So the acting-as itself is recorded, start and stop, and the spans are laid over the timeline.
 */
export interface ActingSpan {
  /** The real account. */
  actor: string;
  /** Who he was acting as. */
  as: string;
  from: string;
  /** Open-ended when the session ended without a stop — a closed tab writes nothing. */
  to?: string;
}

export function actingSpans(entries: readonly AuditEntry[]): ActingSpan[] {
  const open = new Map<string, ActingSpan>();
  const out: ActingSpan[] = [];
  for (const e of [...entries].sort((a, b) => a.at.localeCompare(b.at))) {
    if (e.kind !== 'acting-as') continue;
    const as = (e.onBehalfOf ?? '').toUpperCase();
    const actor = String(e.actor).toUpperCase();
    const key = `${actor}:${as}`;
    if (e.span === 'start') {
      const span: ActingSpan = { actor, as, from: e.at };
      open.set(key, span);
      out.push(span);
    } else if (e.span === 'stop') {
      const span = open.get(key);
      if (span) { span.to = e.at; open.delete(key); }
    }
  }
  return out;
}

/**
 * Lay the spans over the timeline.
 *
 * An event is annotated when it names the impersonated manager and falls inside a span. It is an
 * INFERENCE — the record itself does not say who typed it — so `viaInferred` is set and the screen
 * says so. Where a record DOES carry its saver, `actor` is already right and there is nothing to
 * infer: the guard is that the event's actor must be the impersonated man, not the real one.
 *
 * A span with no `to` is treated as open. That is the honest reading of a browser that was closed
 * rather than switched back, and it errs towards saying "Tim may have been at the keyboard", which
 * is the direction an audit log should err in.
 */
/**
 * An admin saying, afterwards, who was really at the keyboard.
 *
 * The span inference only reaches back as far as the acting-as log, which starts the day it ships.
 * Everything before that — the 18 August farm nomination Tim entered while impersonating John — has
 * no span to overlap and would read as John's own act for ever.
 *
 * So an admin can annotate a row. That is a THIRD kind of provenance and it is labelled as such:
 * not read off the record, not inferred from a span, but claimed by a person, with his name and
 * the time on it. The note is a row in the append-only log like any other, so the claim is itself
 * auditable — and a later note supersedes an earlier one rather than overwriting it, because
 * nothing in this collection is ever overwritten.
 */
export const VIA_NOTE = 'via-note';

export function viaNotes(entries: readonly AuditEntry[]): Map<string, AuditEntry> {
  const by = new Map<string, AuditEntry>();
  for (const e of [...entries].sort((a, b) => a.at.localeCompare(b.at))) {
    if (e.kind !== 'note' || !e.target?.startsWith(`${VIA_NOTE}:`)) continue;
    // Last one wins. Both are kept in the collection; only the newest is applied.
    by.set(e.target.slice(VIA_NOTE.length + 1), e);
  }
  return by;
}

export function attributeNotes(
  events: readonly AuditEvent[], notes: ReadonlyMap<string, AuditEntry>,
): AuditEvent[] {
  if (!notes.size) return [...events];
  return events.map((e) => {
    const n = notes.get(e.id);
    const via = n?.detail?.Via;
    if (!n || !via) return e;
    return {
      ...e,
      via: via.toUpperCase(),
      // A person's claim, not a guess from a span — so `viaInferred` comes OFF.
      viaInferred: false,
      viaNotedAt: n.at,
      ...(n.detail?.Why ? { viaWhy: n.detail.Why } : {}),
    };
  });
}

export function attributeVia(
  events: readonly AuditEvent[], spans: readonly ActingSpan[],
): AuditEvent[] {
  if (!spans.length) return [...events];
  return events.map((e) => {
    if (e.source !== 'derived' || e.via) return e;
    const span = spans.find((sp) => sp.as === e.actor
      && e.at >= sp.from && (sp.to === undefined || e.at <= sp.to));
    if (!span || span.actor === e.actor) return e;
    return { ...e, via: span.actor, viaInferred: true };
  });
}

/**
 * One timeline, newest first.
 *
 * WHERE THE TWO HALVES OVERLAP THE DERIVED EVENT WINS. A recorded row is one browser's claim that
 * something happened; the derived event is read off the record that thing produced. If they ever
 * disagree the record is the answer, and showing both would be showing the same act twice with no
 * way to tell which to believe.
 */
export function mergeEvents(
  derived: readonly AuditEvent[], recorded: readonly AuditEvent[],
): AuditEvent[] {
  const seen = new Set(derived.map((e) => e.id));

  /*
   * THE ONE EXCEPTION, AND IT RUNS THE OTHER WAY: a keeper slate.
   *
   * A declaration holds only its current state, so the derived event can count the slate and can
   * never say what changed — `Submitted 6 keepers for 2026`, which is what sent Tim looking
   * through the log for what Amit had actually done (31 August). The recorded row was written by
   * the screen that still had the previous version and says `Added Jonathan Brooks`.
   *
   * They describe the SAME ACT — the save stamps both with one instant — so printing both is
   * printing it twice, and of the two the recorded one is strictly more informative. The record
   * it describes is still in the database for anybody who wants to check it, which is the
   * safeguard the derived-wins rule exists to provide.
   *
   * Matched on target AND instant, never on target alone: two saves a minute apart are two acts.
   */
  const superseded = new Set(
    recorded.filter((e) => e.kind === 'keeper' && e.target).map((e) => `${e.target}@${e.at}`),
  );

  const kept = derived.filter(
    (e) => !(e.kind === 'keeper' && e.target && superseded.has(`${e.target}@${e.at}`)),
  );

  return [...kept, ...recorded.filter((e) => !seen.has(e.id))]
    .sort((a, b) => b.at.localeCompare(a.at) || a.id.localeCompare(b.id));
}

/* -- reading it -------------------------------------------------------------------------------- */

export interface AuditFilter {
  kind?: EventKind | 'all';
  actor?: string | 'all';
  /** Matches the sentence, the actor and the target together. */
  q?: string;
  /** ISO dates, inclusive. */
  from?: string;
  to?: string;
}

export function filterEvents(
  events: readonly AuditEvent[], f: AuditFilter,
): AuditEvent[] {
  const q = (f.q ?? '').trim().toLowerCase();
  return events.filter((e) => {
    if (f.kind && f.kind !== 'all' && e.kind !== f.kind) return false;
    /*
     * Three ways a man is IN an event: he did it, it was his record, or he was the hand behind
     * somebody else's name. "What has Tim been doing" has to find the third, or the filter hides
     * exactly the rows impersonation makes worth looking for.
     */
    if (f.actor && f.actor !== 'all') {
      const who = f.actor.toUpperCase();
      if (e.actor !== who && e.onBehalfOf !== who && e.via !== who) return false;
    }
    if (f.from && e.at.slice(0, 10) < f.from) return false;
    if (f.to && e.at.slice(0, 10) > f.to) return false;
    if (!q) return true;
    return [e.what, e.actor, e.onBehalfOf ?? '', e.target ?? '']
      .some((x) => x.toLowerCase().includes(q));
  });
}

/** Day groups, newest first, for a list nobody wants to read as 4,000 undivided rows. */
export function eventsByDay(
  events: readonly AuditEvent[],
  tz: string = 'UTC',
): Array<{ day: string; events: AuditEvent[] }> {
  const days = new Map<string, AuditEvent[]>();
  for (const e of events) {
    /*
     * The day this happened ON, in the zone the reader chose — not the day the UTC stamp starts
     * with. From about 5pm Mountain the two disagree, which is most of a draft night.
     */
    const day = dayIn(e.at, tz);
    const list = days.get(day);
    if (list) list.push(e);
    else days.set(day, [e]);
  }
  return [...days.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([day, list]) => ({ day, events: list }));
}

/** How many of each kind are in view — the chip row's counts. */
export function countByKind(events: readonly AuditEvent[]): Map<EventKind, number> {
  const by = new Map<EventKind, number>();
  for (const e of events) by.set(e.kind, (by.get(e.kind) ?? 0) + 1);
  return by;
}

/** Everybody who appears, for the actor filter. */
export function actorsIn(events: readonly AuditEvent[]): string[] {
  return [...new Set(events.flatMap((e) => [
    e.actor, ...(e.onBehalfOf ? [e.onBehalfOf] : []), ...(e.via ? [e.via] : []),
  ]))].sort();
}

/* -- taking it away ---------------------------------------------------------------------------- */

const CSV_CELL = (s: string) => `"${s.replace(/"/g, '""')}"`;

/**
 * CSV, because the question that follows "who changed this" is usually asked in a spreadsheet,
 * and because an audit log you cannot take out of the app is one you cannot show anybody.
 */
export function toCsv(events: readonly AuditEvent[]): string {
  const head = ['when', 'who', 'really', 'on behalf of', 'kind', 'what', 'target', 'source', 'detail'];
  const rows = events.map((e) => [
    e.at,
    e.actor,
    // "really" carries the inference marker with it, so a spreadsheet cannot lose the caveat.
    e.via ? `${e.via}${e.viaInferred ? ' (inferred)' : ''}` : '',
    e.onBehalfOf ?? '',
    KIND_LABEL[e.kind], e.what, e.target ?? '', e.source,
    (e.detail ?? []).map((x) => `${x.label}: ${x.value}`).join(' | '),
  ].map(CSV_CELL).join(','));
  return [head.map(CSV_CELL).join(','), ...rows].join('\n');
}
